import { useEffect, useState, type FormEvent } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { ExternalLinkIcon } from '../components/ExternalLinkIcon';
import { OAuthButtons } from '../components/OAuthButtons';
import { Turnstile, turnstileActive } from '../components/Turnstile';
import { isIntentPlan, savePlanIntent } from '../lib/intent';
import { TERMS_VERSION, WILLOW_HOME } from '../lib/links';
import { SaasError, saasResendVerification } from '../lib/saas-client';

function safeReturn(raw: string | null): string {
  if (!raw) return '/account';
  try {
    const decoded = decodeURIComponent(raw);
    if (decoded.startsWith('/') && !decoded.startsWith('//')) return decoded;
  } catch {
    // fall through
  }
  return '/account';
}

const PLAN_NAMES: Record<string, string> = {
  payg: 'Pay as you go',
  builder: 'Builder',
  scale: 'Scale',
};

export function SignupPanel() {
  const { user, signup } = useAuth();
  const [params] = useSearchParams();
  const returnTo = safeReturn(params.get('return'));
  const rawPlan = params.get('plan');
  const plan = isIntentPlan(rawPlan) ? rawPlan : null;
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newsletter, setNewsletter] = useState(false);
  const [token, setToken] = useState('');
  const [resendToken, setResendToken] = useState('');
  const [captchaKey, setCaptchaKey] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [resent, setResent] = useState(false);

  // The plan picked on the pricing page waits until they're signed in.
  useEffect(() => {
    if (plan) savePlanIntent(plan);
  }, [plan]);

  if (user) return <Navigate to={plan ? `/billing?plan=${plan}` : returnTo} replace />;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      // This form shows the agreement line below, so it says which Terms that was.
      await signup(email.trim(), password, newsletter, token || undefined, TERMS_VERSION);
      setSubmitted(true);
    } catch (err) {
      // Turnstile tokens are single-use; reissue a fresh one for the retry.
      setToken('');
      setCaptchaKey((k) => k + 1);
      setError(err instanceof SaasError ? err.message : 'Sign-up failed.');
    } finally {
      setBusy(false);
    }
  }

  async function onResend() {
    setError(null);
    setResent(false);
    setBusy(true);
    try {
      await saasResendVerification(email.trim(), resendToken || undefined);
      setResent(true);
    } catch (err) {
      setResendToken('');
      setCaptchaKey((k) => k + 1);
      setError(err instanceof SaasError ? err.message : 'Could not resend the email.');
    } finally {
      setBusy(false);
    }
  }

  if (submitted) {
    return (
      <div className="auth-page">
        <section className="panel auth-panel">
          <header className="page-header">
            <h1>Check your email</h1>
            <p>
              We sent a link to <strong>{email.trim()}</strong>. Open it to verify your
              address, then sign in to create your first key.
            </p>
          </header>
          {resent ? (
            <p className="form-success">We sent the link again.</p>
          ) : (
            <>
              <p className="muted">Didn't get it? Look in spam, or send it again.</p>
              <Turnstile key={captchaKey} onVerify={setResendToken} />
              <button
                type="button"
                className="btn-link"
                onClick={onResend}
                disabled={busy || (turnstileActive && !resendToken)}
              >
                {busy ? 'Sending…' : 'Send the link again'}
              </button>
            </>
          )}
          {error && <p className="form-error">{error}</p>}
          <p className="auth-switch">
            <Link to={`/login?return=${encodeURIComponent(returnTo)}`}>Back to sign in</Link>
          </p>
        </section>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <section className="panel auth-panel">
        <header className="page-header">
          <h1>Get an API key</h1>
          <p>
            {plan ? (
              <>
                Create your account, then pick up <strong>{PLAN_NAMES[plan]}</strong> on the
                billing page.
              </>
            ) : (
              <>
                The Free plan includes 10M units a month (most calls are one unit) at 25
                requests a second. No card needed.
              </>
            )}
          </p>
        </header>
        <OAuthButtons verb="Sign up" />
        <form onSubmit={onSubmit} className="auth-form">
          <label>
            <span>Email</span>
            <input
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>
          <label>
            <span>Password</span>
            <input
              type="password"
              required
              minLength={12}
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <small className="muted">At least 12 characters.</small>
          </label>
          <label className="checkbox-row">
            <input
              type="checkbox"
              checked={newsletter}
              onChange={(e) => setNewsletter(e.target.checked)}
            />
            <span>Send me occasional updates about Willow. Unsubscribe any time.</span>
          </label>
          <Turnstile key={captchaKey} onVerify={setToken} />
          {error && <p className="form-error">{error}</p>}
          <button
            type="submit"
            className="btn btn-primary"
            disabled={busy || (turnstileActive && !token)}
          >
            {busy ? 'Creating account…' : 'Create account'}
          </button>
        </form>
        {/* Covers the GitHub and Google buttons too, so it sits under all of them. */}
        <p className="auth-agreement" data-testid="signup-agreement">
          By creating an account, you agree to the{' '}
          <a href={`${WILLOW_HOME}/terms`} target="_blank" rel="noreferrer">
            Terms of <ExternalLinkIcon>Service</ExternalLinkIcon>
          </a>{' '}
          and acknowledge the{' '}
          <a href={`${WILLOW_HOME}/privacy`} target="_blank" rel="noreferrer">
            Privacy <ExternalLinkIcon>Policy</ExternalLinkIcon>
          </a>
          .
        </p>
        <p className="auth-switch">
          Already have an account?{' '}
          <Link to={`/login?return=${encodeURIComponent(returnTo)}`}>Sign in</Link>
        </p>
      </section>
    </div>
  );
}
